<template>
  <nav class="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-t-md z-[90] h-18 flex items-center justify-around px-1">
    <router-link
      v-for="item in visibleNavigationItems"
      :key="item.name"
      :to="item.to"
      :class="[
        'flex flex-col items-center justify-center px-1 py-2 rounded-lg text-xs font-medium h-full transition-all duration-200',
        getItemWidthClass(),
        isActive(item.to) 
          ? 'bg-indigo-100 text-indigo-700' 
          : 'text-gray-600 hover:bg-indigo-50 hover:text-indigo-600'
      ]"
      @click="closeMobileMenu"
    >
      <component :is="item.icon" class="h-6 w-6 mb-1" />
      <span class="truncate">{{ $t(`agentDashboard.${item.name}`) }}</span>
    </router-link>
  </nav>
</template>

<script setup>
import { useRoute } from 'vue-router';
import { ref, onMounted, computed } from 'vue';
import { HomeIcon, UsersIcon, DocumentTextIcon, BuildingOffice2Icon, CalendarDaysIcon, UserCircleIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import { isCurrentUserBasicAgent } from '../../utils/agentRoleUtils';

const route = useRoute();

// Estado para tipo de agente
const isBasicAgent = ref(false);

// Elementos de navegación en el orden requerido: "inicio", "Propiedades", "Colivings", "Perfil", "Clientes", "Calendario"
const navigationItems = [
  { name: 'navigation.home', to: '/agent-dashboard', icon: HomeIcon },
  // { name: 'navigation.properties', to: '/agent-dashboard/contracts', icon: DocumentTextIcon },
  { name: 'navigation.colivings', to: '/agent-dashboard/colivings', icon: BuildingOffice2Icon },
  // { name: 'navigation.incidents', to: '/agent-dashboard/incidents', icon: ExclamationTriangleIcon },
  { name: 'navigation.profile', to: '/agent-dashboard/profile', icon: UserCircleIcon },
  { name: 'navigation.clients', to: '/agent-dashboard/clients', icon: UsersIcon, requiresPro: true },
  { name: 'navigation.calendar', to: '/agent-dashboard/calendar', icon: CalendarDaysIcon, requiresPro: true },
];

// Computed para elementos visibles según el tipo de agente
const visibleNavigationItems = computed(() => {
  if (isBasicAgent.value) {
    // Para agentes Basic: mostrar elementos básicos + perfil, excluir los que requieren Pro
    return navigationItems.filter(item => !item.requiresPro);
  } else {
    // Para agentes Pro/Master: mostrar todos excepto los exclusivos para Basic
    return navigationItems.filter(item => !item.onlyForBasic);
  }
});

// Función para determinar el ancho de cada elemento según la cantidad visible
const getItemWidthClass = () => {
  const itemCount = visibleNavigationItems.value.length;
  const widthClasses = {
    3: 'w-1/3',
    4: 'w-1/4',
    5: 'w-1/5',
    6: 'w-1/6'
  };
  return widthClasses[itemCount] || 'w-1/6';
};

const isActive = (path) => {
  if (path === '/agent-dashboard') {
    return route.path === path;
  }
  return route.path.startsWith(path);
};

// Función para verificar tipo de agente
const checkAgentType = async () => {
  try {
    isBasicAgent.value = await isCurrentUserBasicAgent();
  } catch (error) {
    console.error('[AgentMobileNavbar] Error verificando tipo de agente:', error);
  }
};

// Esta función sería útil si este menú interactuara con un menú hamburguesa global,
// pero por ahora puede estar vacía o usarse para alguna lógica al hacer clic si es necesario.
const closeMobileMenu = () => {
  // Lógica para cerrar un menú móvil principal si fuera necesario
};

// Verificar tipo de agente al montar el componente
onMounted(async () => {
  await checkAgentType();
});
</script>

<style scoped>
/* A custom utility for top shadow, as Tailwind doesn't have it by default */
.shadow-t-md {
  box-shadow: 0 -4px 6px -1px rgb(0 0 0 / 0.1), 0 -2px 4px -2px rgb(0 0 0 / 0.1);
}

nav a span {
  display: block;
  white-space: nowrap; /* Evitar que el texto se divida en dos líneas si es muy corto */
}
</style> 